import { useEffect, useState } from "react";
import { Trophy, Users, ClipboardList, X, Rocket, FileDown, Video } from "lucide-react";
import axios from "axios";

const STORAGE_KEY = "sudeporte_onboarding_v1_done";
const API = process.env.REACT_APP_BACKEND_URL;
// In-memory backup: if localStorage is unavailable (private mode / iframe),
// the dismiss still persists for the whole SPA session so the modal never
// re-appears on navigation.
const dismissedRoles = new Set();

const STEPS_BY_ROLE = {
  super_admin: [
    { icon: Trophy, title: "Bienvenido/a a Sudefootball", body: "Desde 'Plataforma' controlas clubes, planes, monitorización de emails y todo el material de marketing y ventas." },
    { icon: Users, title: "Alta de clubes", body: "Crea un club y su admin recibirá automáticamente el Kit de Bienvenida por email con la guía y los primeros pasos." },
    { icon: Video, title: "Graba tu vídeo de onboarding", body: "Descarga el guion listo para grabar el tutorial de bienvenida que verán los nuevos clubes." },
  ],
  club_admin: [
    { icon: Trophy, title: "Crea tus categorías", body: "Comienza con U8, U10, U12… y elige la plantilla base para cada una." },
    { icon: Users, title: "Añade equipos y staff", body: "Da de alta a los entrenadores, delegados y coordinadores desde la sección Staff." },
    { icon: ClipboardList, title: "El resto es fácil", body: "Los entrenadores añadirán a sus jugadores y comenzarán a registrar jornadas y asistencia." },
  ],
  coordinator: [
    { icon: Trophy, title: "Bienvenido/a Coordinador/a", body: "Tienes una vista global de todos los equipos del club en la sección 'Vista Global'." },
    { icon: ClipboardList, title: "Personaliza plantillas", body: "Ajusta bloques e indicadores de puntuación para cada equipo desde 'Plantillas'." },
    { icon: Users, title: "Explota los datos", body: "Consulta 'Estadísticas' y 'Evaluación' para el seguimiento individual." },
  ],
  coach: [
    { icon: Users, title: "Añade tus jugadores", body: "Entra a 'Mis Equipos' y da de alta la plantilla con nombre, dorsal y contacto de padres." },
    { icon: ClipboardList, title: "Registra jornadas y evalúa", body: "Cada partido registra minutos, goles, disparos y cambios. Las valoraciones técnicas van en 'Evaluación' cuando quieras hacer seguimiento individual." },
    { icon: Rocket, title: "Genera enlaces públicos", body: "Desde la ficha de cada jugador, crea un perfil público para compartir con la familia." },
  ],
};
// Aliases: director_deportivo comparte experiencia con coordinator; delegado con coach.
STEPS_BY_ROLE.director_deportivo = STEPS_BY_ROLE.coordinator;
STEPS_BY_ROLE.delegado = STEPS_BY_ROLE.coach;

export function OnboardingModal({ role, userName }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [guideInfo, setGuideInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingScript, setDownloadingScript] = useState(false);

  useEffect(() => {
    if (!role) return;
    if (dismissedRoles.has(role)) return;
    let done = false;
    try {
      done = !!localStorage.getItem(`${STORAGE_KEY}_${role}`);
    } catch { /* storage unavailable — rely on in-memory set */ }
    if (!done) setOpen(true);
  }, [role]);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (!token) return;
    axios
      .get(`${API}/api/docs/my-guide/info`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (r.data?.available) setGuideInfo(r.data); })
      .catch(() => { /* silently ignore — enlace opcional */ });
  }, [open]);

  const downloadGuide = async () => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (!token || !guideInfo) return;
    try {
      setDownloading(true);
      const res = await axios.get(`${API}/api/docs/my-guide`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = guideInfo.filename || "guia.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const downloadScript = async () => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (!token) return;
    try {
      setDownloadingScript(true);
      const res = await axios.get(`${API}/api/admin/docs/video-script-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "GUION_VIDEO.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingScript(false);
    }
  };

  const steps = STEPS_BY_ROLE[role] || [];
  if (!open || steps.length === 0) return null;
  const s = steps[step];
  const isLast = step === steps.length - 1;

  const close = () => {
    dismissedRoles.add(role);
    try {
      localStorage.setItem(`${STORAGE_KEY}_${role}`, "1");
    } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal" onClick={close}>
      <div className="relative border border-primary/40 bg-card max-w-md w-full p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid="onboarding-close-btn"
          onClick={close}
          className="absolute top-3 right-3 w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors"
          aria-label="Cerrar"
        >
          <X size={14} />
        </button>

        <div className="overline text-primary">Bienvenido/a {userName ? `· ${userName}` : ""}</div>
        <div className="flex items-center gap-4 mt-4">
          <div className="w-14 h-14 border border-primary/60 bg-primary/10 flex items-center justify-center">
            <s.icon size={26} className="text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-black text-2xl uppercase tracking-tight leading-none">{s.title}</h2>
            <div className="overline text-[10px] mt-1">Paso {step + 1} de {steps.length}</div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mt-5 leading-relaxed">{s.body}</p>

        {/* Guide download — always visible on every step so users can grab it whenever they want */}
        {guideInfo && (
          <button
            data-testid="onboarding-download-guide-btn"
            onClick={downloadGuide}
            disabled={downloading}
            className="mt-5 w-full h-11 border border-primary/60 bg-primary/5 hover:bg-primary/10 text-primary text-xs uppercase font-heading tracking-wider transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <FileDown size={14} />
            {downloading ? "Descargando…" : `Descargar PDF · ${guideInfo.label}`}
          </button>
        )}

        {/* Video script download — super admin only */}
        {role === "super_admin" && (
          <button
            data-testid="onboarding-download-script-btn"
            onClick={downloadScript}
            disabled={downloadingScript}
            className="mt-3 w-full h-11 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Video size={14} />
            {downloadingScript ? "Descargando…" : "Descargar guion de vídeo"}
          </button>
        )}

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 w-8 transition-colors ${i === step ? "bg-primary" : "bg-border"}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 mt-6">
          {step > 0 ? (
            <button
              data-testid="onboarding-prev-btn"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider transition-colors"
            >Anterior</button>
          ) : <div />}
          {!isLast ? (
            <button
              data-testid="onboarding-next-btn"
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider transition-colors"
            >Siguiente</button>
          ) : (
            <button
              data-testid="onboarding-finish-btn"
              onClick={close}
              className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider transition-colors inline-flex items-center gap-2"
            ><Rocket size={13} /> ¡Empezar!</button>
          )}
        </div>
      </div>
    </div>
  );
}
